import React, { memo } from 'react';
import { GEM_META, GEM_CODES } from '../constants';

/**
 * Match-3 pieces (v1.47.0): our game items — hero head / key / chest / ticket / GRAM (GEM_META.icon) on a glossy
 * tile of the piece colour, drawn around (0,0) inside a 100-unit cell. Specials («combo» pieces) keep the item
 * and get a mark on top: LINE_H / LINE_V (stripes + arrows), BOMB (glowing fuse ring), RAINBOW (spinning orb
 * with all items). Gradients come from <GemDefs/> (rendered once per <svg>).
 */

function ItemTile({ gem }) {
  const meta = GEM_META[gem] || GEM_META.STR;
  return (
    <g className="m3-gem__body">
      <rect x="-42" y="-42" width="84" height="84" rx="22" fill={`url(#m3-grad-${gem})`} stroke={meta.dark} strokeWidth="4" />
      <rect x="-34" y="-36" width="68" height="30" rx="15" fill="#ffffff" opacity="0.18" />
      <image href={meta.icon} x="-33" y="-33" width="66" height="66" preserveAspectRatio="xMidYMid meet" />
    </g>
  );
}

function RainbowOrb() {
  const n = GEM_CODES.length;
  const r = 38;
  const sectors = GEM_CODES.map((code, i) => {
    const a0 = (i * 2 * Math.PI) / n;
    const a1 = ((i + 1) * 2 * Math.PI) / n;
    const d = `M0 0 L${(Math.cos(a0) * r).toFixed(1)} ${(Math.sin(a0) * r).toFixed(1)} A${r} ${r} 0 0 1 ${(Math.cos(a1) * r).toFixed(1)} ${(Math.sin(a1) * r).toFixed(1)}Z`;
    return <path key={code} d={d} fill={GEM_META[code].base} />;
  });
  return (
    <g className="m3-orb">
      <circle r="44" fill="url(#m3-orb-glow)" />
      <g className="m3-orb__spin">{sectors}</g>
      <circle r={r} fill="url(#m3-orb-shine)" stroke="#ffffff" strokeWidth="3" />
      <circle r="20" fill="#ffffff" opacity="0.92" />
      <image href="/gram.png" x="-16" y="-16" width="32" height="32" />
      <path d="M-24 -28 l2 -6 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2z" fill="#fff" />
    </g>
  );
}

function LineMark({ vertical }) {
  // stripes go over the item picture semi-transparent, so the item stays recognisable
  return (
    <g className="m3-line" transform={vertical ? 'rotate(90)' : undefined}>
      {[-24, 24].map((y) => (
        <rect key={y} x="-38" y={y - 4} width="76" height="8" rx="4" fill="#ffffff" opacity="0.85" stroke="#1b1236" strokeWidth="1.5" />
      ))}
      <path d="M-47 0 l9 -9 v18z M47 0 l-9 -9 v18z" fill="#ffffff" stroke="#1b1236" strokeWidth="2" strokeLinejoin="round" />
    </g>
  );
}

function BombMark() {
  return (
    <g className="m3-bomb">
      <circle className="m3-bomb__ring" r="44" fill="none" stroke="#ffb443" strokeWidth="5" strokeDasharray="10 7" />
      <path d="M22 -30 q10 -10 18 -6" stroke="#3b2a12" strokeWidth="4" fill="none" strokeLinecap="round" />
      <circle className="m3-bomb__spark" cx="41" cy="-37" r="6" fill="#fff6a8" />
    </g>
  );
}

export const GemArt = memo(function GemArt({ gem, special }) {
  if (special === 'RAINBOW' || gem === 'RAINBOW') return <RainbowOrb />;
  return (
    <g className={`m3-gem m3-gem--${gem}${special ? ` m3-gem--${special}` : ''}`}>
      {special === 'BOMB' && <circle className="m3-bomb__glow" r="48" fill="url(#m3-bomb-glow)" />}
      <ItemTile gem={gem} />
      {special === 'LINE_H' && <LineMark />}
      {special === 'LINE_V' && <LineMark vertical />}
      {special === 'BOMB' && <BombMark />}
    </g>
  );
});

/** Gradients used by GemArt — render once inside every <svg> that draws gems. */
export function GemDefs() {
  return (
    <defs>
      {Object.entries(GEM_META).map(([code, m]) => (
        <linearGradient key={code} id={`m3-grad-${code}`} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor={m.light} />
          <stop offset="0.45" stopColor={m.base} />
          <stop offset="1" stopColor={m.dark} />
        </linearGradient>
      ))}
      <radialGradient id="m3-orb-glow">
        <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="m3-orb-shine" cx="0.35" cy="0.3" r="0.75">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
        <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.1" />
        <stop offset="1" stopColor="#1b1236" stopOpacity="0.35" />
      </radialGradient>
      <radialGradient id="m3-bomb-glow">
        <stop offset="0.5" stopColor="#ff8a1f" stopOpacity="0.7" />
        <stop offset="1" stopColor="#ff8a1f" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
}

/** Small standalone gem (HUD chips). */
export function GemIcon({ gem, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="-50 -50 100 100" aria-hidden="true" className="m3-gem-icon">
      <GemDefs />
      <GemArt gem={gem} />
    </svg>
  );
}

export default GemArt;
